import { useContext, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { ScrollArea, ScrollBar } from '@repo/videa-components/src/index';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components/src/ui/tabs';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ThemeModeContext } from '../casePresentation/patientDownloadable/PreviewDialog/QRPrintDownloadContent';

import { TreatmentTabContent } from './TreatmentTabContent';

export const pediatricTreatmentKeys = [
  'crown_pediatric',
  'enamel_regen_pediatric',
  'fluoride',
  'spacer',
  'sealant',
] as const;
export type PediatricTreatmentKey = (typeof pediatricTreatmentKeys)[number];

interface PediatricTreatmentOption {
  description: string;
  fallback: string;
  label: string;
  src: string;
  value: PediatricTreatmentKey;
  isPediatric: true;
}

export const usePediatricTreatmentOptions = (
  getDefault = false,
): Partial<Record<PediatricTreatmentKey, PediatricTreatmentOption>> => {
  const { t } = useLingui();
  const {
    i18n: { locale },
  } = useLingui();
  const { data: preferences } = usePreferences();

  const customCopy = preferences?.customPatientEducationTxCopy;

  const defaultTreatments: Partial<
    Record<PediatricTreatmentKey, PediatricTreatmentOption>
  > = {
    fluoride: {
      label: t`Fluoride (Preventative)`,
      description: t`Fluoride treatment is a simple and effective way to help protect your child's teeth from cavities. Fluoride is a natural mineral that strengthens tooth enamel, making it more resistant to decay. After your dental cleaning, the dental professional applies fluoride—usually as a gel, foam, or varnish—directly to your child's teeth. It's quick, painless, and especially helpful for individuals who are more prone to cavities. Regular fluoride treatments can keep your teeth strong and healthy between dental visits.`,
      value: 'fluoride',
      src: 'assets/images/patientEducation/pediatric-fluoride.png',
      fallback: 'assets/images/patientEducation/pediatric-fluoride.png',
      isPediatric: true,
    },
    enamel_regen_pediatric: {
      label: t`Tooth Enamel Repair (Incipient Lesion)`,
      description: t`An incipient lesion is an early sign of tooth decay that appears in the enamel, the outer surface of the tooth, often as small white or off-white spots on your child's tooth. It means the enamel is starting to weaken, but the cavity hasn't fully formed yet. We treat it by gently cleaning the area and preparing it to receive the enamel repair treatment within the same appointment. This will help restore the enamel and stop the decay from progressing—no drilling or filling needed. It's a quick, painless way to keep your child's teeth healthy and strong.`,
      value: 'enamel_regen_pediatric',
      src: `assets/images/patientEducation/pediatric-enamelRegen-${locale}.png`,
      fallback: `assets/images/patientEducation/pediatric-enamelRegen-en.png`,
      isPediatric: true,
    },
    crown_pediatric: {
      label: t`Stainless Steel / Zirconia Crown`,
      description: t`Stainless steel crowns are pre-made metal caps we place over a child's damaged or decayed molar to fully protect it—with minimal to no drilling or multiple visits needed. They're ultra-durable, done in one appointment, and prevent further decay while keeping the tooth's shape and function. For a more natural look, pre-made zirconia crowns are an option—strong, tooth-colored caps that blend seamlessly with your child's smile. Both options are designed to safeguard baby teeth until they're ready to fall out on their own.`,
      value: 'crown_pediatric',
      src: `assets/images/patientEducation/pediatric-crown-${locale}.png`,
      fallback: `assets/images/patientEducation/pediatric-crown-en.png`,
      isPediatric: true,
    },
    spacer: {
      label: t`Space Maintainer`,
      description: t`A space maintainer is a small dental device used to maintain the space for the permanent tooth when a child loses a baby tooth too early. It helps guide the adult tooth into the right position and prevents other teeth from shifting. Placing a space maintainer is quick and comfortable, and it plays an important role in keeping your child's smile healthy and aligned as they grow.`,
      value: 'spacer',
      src: `assets/images/patientEducation/pediatric-spacer-${locale}.png`,
      fallback: 'assets/images/patientEducation/pediatric-spacer-en.png',
      isPediatric: true,
    },
    sealant: {
      label: t`Sealant`,
      description: t`Dental sealants are thin, protective coatings applied to the chewing surfaces of your child's back teeth to help prevent cavities. They quickly bond to the grooves of the teeth, creating a smooth surface that keeps out food and bacteria. The process is fast, painless, and helps protect your child's teeth during the cavity-prone years.`,
      value: 'sealant',
      src: `assets/images/patientEducation/pediatric-sealant-${locale}.png`,
      fallback: 'assets/images/patientEducation/pediatric-sealant-en.png',
      isPediatric: true,
    },
  };

  // If custom copy is available, merge it with default treatments
  if (customCopy && !getDefault) {
    return {
      ...defaultTreatments,
      ...Object.keys(customCopy).reduce(
        (acc, key) => {
          // Check if the key exists in our pediatric treatments and in customCopy
          if (key in defaultTreatments) {
            const treatmentKey = key as keyof typeof defaultTreatments;
            if (customCopy[treatmentKey] && defaultTreatments[treatmentKey]) {
              acc[treatmentKey] = {
                ...defaultTreatments[treatmentKey],
                description: customCopy[treatmentKey]!,
              };
            }
          }
          return acc;
        },
        {} as typeof defaultTreatments,
      ),
    };
  }

  return defaultTreatments;
};

interface Props {
  imageId?: string;
}

export const PediatricTabs = ({ imageId }: Props) => {
  const [selectedTab, setSelectedTab] =
    useState<PediatricTreatmentKey>('fluoride');

  const tabs = usePediatricTreatmentOptions();
  const { trackEvent } = useTrackEvent();
  const mode = useContext(ThemeModeContext);
  const { isIframe, externalPatientId, externalImageId, patientId } =
    useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  return (
    <Tabs
      value={selectedTab}
      onValueChange={(value) => {
        setSelectedTab(value as PediatricTreatmentKey);
        trackEvent(mixpanelEvents.patientEducationTreatmentTabChange, {
          patientId: selectedPatientId ?? patientId,
          imageId,
          isIframe: !!isIframe,
          externalPatientId,
          externalImageId,
          tab: value,
        });
      }}
    >
      <ScrollArea>
        <div className="vai-w-full vai-relative vai-h-14">
          <TabsList
            className={classNames(
              'vai-absolute vai-max-w-[100%] vai-bg-transparent vai-border-b vai-rounded-none vai-pb-0 vai-px-0 vai-h-auto vai-overflow-x-auto',
              {
                'vai-border-zinc-700': mode === 'dark',
                'vai-border-zinc-200': mode === 'light',
              },
            )}
          >
            {Object.values(tabs).map(({ label, value }) => (
              <TabsTrigger
                className={classNames(
                  'vai-pt-2 vai-pb-3 data-[state=active]:vai-bg-transparent vai-border-b-2 vai-border-transparent data-[state=active]:vai-border-[#2368F6] !vai-rounded-none !vai-shadow-none',
                  {
                    'data-[state=active]:vai-text-zinc-50 vai-text-zinc-400':
                      mode === 'dark',
                    'data-[state=active]:vai-text-zinc-900 vai-text-zinc-500':
                      mode === 'light',
                  },
                )}
                key={value}
                value={value}
                data-testid={`tab-trigger-pediatric-${value}`}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {Object.values(tabs).map(({ value, fallback, src }) => (
        <TreatmentTabContent
          fallback={fallback ?? ''}
          key={value}
          tabKey={value}
          src={src ?? ''}
        />
      ))}
    </Tabs>
  );
};
